/*
 * INDRAAZ — "Build your own box" (replaces the old Rapi box-builder page).
 *
 * Every +/- tap on a tile only updates a local, page-only "quantities"
 * staging area — nothing is added to the real Shopify cart until the
 * customer taps "Checkout". At that point every picked item is added in
 * ONE bulk /cart/add.js request, and the customer is sent straight to
 * checkout. There is exactly one place in this file that ever calls
 * /cart/add.js: submitBoxToCart().
 *
 * The discounted "pick any N for ₹X" price is still only ever a preview
 * computed here for display — same caveat as always: a real Shopify
 * Automatic Discount is what actually enforces it at checkout, and that
 * discount evaluates the cart's real contents regardless of how items got
 * added.
 *
 * Supports multiple tiers per box (e.g. "3 for ₹399" AND "6 for ₹699" for
 * the same product pool, via the page's box_tiers metafield) — see the
 * tiers/currentTier()/nextTier() setup at the top of init(). Adding is
 * allowed all the way up to the HIGHEST tier's quantity (not capped at a
 * lower one, since the point is upselling toward the max) and stops once
 * that's reached. "Checkout" itself appears as soon as the FIRST tier is
 * reached — every tier is a valid, checkout-able box on its own.
 */
(function () {
  if (window.__idzBoxBound) return;
  window.__idzBoxBound = true;

  function formatMoney(cents) {
    // en-IN grouping ("1,199" not "1199", "1,23,456" not "123456") to match
    // how prices are formatted everywhere else on the site.
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function discountedTotal(base, type, value) {
    if (type === 'fixed') {
      return Math.max(0, (parseFloat(value) || 0) * 100);
    }
    var pct = parseFloat(value) || 0;
    return Math.round(base * (1 - pct / 100));
  }

  // Same confetti vocabulary as the cart drawer's reward-progress bar
  // (idz-cart-progress.js/.css) — reusing its .idz-cr__confetti* classes so
  // this reads as the same celebration, not a different effect — but as a
  // fixed full-viewport overlay instead of one scoped to a small drawer
  // panel, since a box page's product grid can run far taller than a
  // screen and a drawer-style panel-relative burst wouldn't read the same
  // way here.
  var CONFETTI_COLORS = ['#241b4e', '#ff6f91', '#ffdd8a', '#4ad991', '#5ec8f2', '#ff9f5a', '#c9a6ff', '#ffffff'];
  var CONFETTI_SHAPES = ['ribbon', 'ribbon', 'dot'];
  var CONFETTI_COUNT = 100;

  function confettiPiece(container, index, panelWidth, panelHeight, originY) {
    var shape = CONFETTI_SHAPES[Math.floor(Math.random() * CONFETTI_SHAPES.length)];
    var piece = document.createElement('span');
    piece.className = 'idz-cr__confetti-piece idz-cr__confetti-piece--' + shape;

    piece.style.setProperty('--idz-cf-color', CONFETTI_COLORS[index % CONFETTI_COLORS.length]);
    piece.style.setProperty('--idz-cf-duration', (2.15 + Math.random() * 0.65).toFixed(2) + 's');
    piece.style.setProperty('--idz-cf-delay', (Math.random() * 0.2).toFixed(2) + 's');

    var burstX = (Math.random() - 0.5) * panelWidth * 0.95;
    var topMargin = 16 + Math.random() * 40;
    var burstY = topMargin - originY;
    var endX = burstX + (Math.random() - 0.5) * 40;
    var bottomOvershoot = panelHeight * (1.08 + Math.random() * 0.18);
    var endY = bottomOvershoot - originY;

    piece.style.setProperty('--idz-cf-burst-x', burstX.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-burst-y', burstY.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-end-x', endX.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-end-y', endY.toFixed(0) + 'px');
    piece.style.setProperty('--idz-cf-rot-1', Math.round(Math.random() * 140 - 70) + 'deg');
    piece.style.setProperty('--idz-cf-rot-3', Math.round(420 + Math.random() * 260) + 'deg');

    if (shape === 'dot') {
      var dotSize = (0.5 + Math.random() * 0.4).toFixed(2) + 'rem';
      piece.style.setProperty('--idz-cf-w', dotSize);
      piece.style.setProperty('--idz-cf-h', dotSize);
    } else {
      piece.style.setProperty('--idz-cf-w', (0.6 + Math.random() * 0.3).toFixed(2) + 'rem');
      piece.style.setProperty('--idz-cf-h', (1.1 + Math.random() * 0.6).toFixed(2) + 'rem');
    }

    container.appendChild(piece);
  }

  function confetti() {
    var panelWidth = Math.max(window.innerWidth, 280);
    var panelHeight = Math.max(window.innerHeight, 400);
    var originY = panelHeight * 0.8;

    var container = document.createElement('div');
    container.className = 'idz-cr__confetti idz-box__confetti';
    // The shared .idz-cr__confetti class (idz-cart-progress.css) only sets
    // `position: fixed` now, not a full-viewport `inset: 0` — that
    // shorthand got dropped when the cart drawer's own confetti moved from
    // being absolutely positioned inside the drawer panel to being fixed
    // and explicitly sized/positioned by JS (see idz-cart-progress.js's
    // confetti()) instead of just filling its parent. Without an explicit
    // size here too, this container collapsed to 0x0 (its pieces are all
    // absolutely positioned, so they don't contribute to its auto size)
    // and `overflow: hidden` clipped every piece — the burst was still
    // running, just invisible. Set explicitly here the same way.
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = panelWidth + 'px';
    container.style.height = panelHeight + 'px';
    container.style.setProperty('--idz-cf-origin-y', originY + 'px');
    container.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < CONFETTI_COUNT; i++) {
      confettiPiece(container, i, panelWidth, panelHeight, originY);
    }
    document.body.appendChild(container);

    window.setTimeout(function () {
      container.remove();
    }, 3400);
  }

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Guarantees the pulse fires and the clone is cleaned up exactly once,
  // even if `transitionend` never arrives — Safari/iOS and some Android
  // WebViews can drop the event on a backgrounded tab, an interrupted
  // transition (fast repeated taps), or a page navigation mid-flight. A
  // timer fallback (a little past the animation's own duration) means nose
  // never gets stuck as a permanent, invisible, off-screen DOM node.
  // The landing target (the "View box" button) has its own ongoing pulsing
  // "tap me" hint animation — the drop-landing bounce is temporary, so the
  // class is removed again once it's done playing, letting that idle hint
  // resume automatically rather than permanently overriding it.
  function pulseLanding(landingEl) {
    landingEl.classList.remove('idz-box__drop-land');
    void landingEl.offsetWidth;
    landingEl.classList.add('idz-box__drop-land');
    window.setTimeout(function () {
      landingEl.classList.remove('idz-box__drop-land');
    }, 500);
  }

  function settleOnce(clone, onLand, fallbackDelay) {
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      clone.remove();
      if (onLand) onLand();
    }
    clone.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, fallbackDelay);
  }

  // The REAL thumbnail (the state update that turns an empty "+" slot into
  // a filled one — see addOne) now only happens once this animation
  // actually lands, via the `onLand` callback, instead of the moment the
  // tap is registered. It used to fire immediately, before the clone even
  // started flying — which meant the real thumbnail popped into that empty
  // slot a full second before the flying copy caught up and landed on top
  // of it, undermining the whole "it fell into that slot" illusion. Three
  // unhurried beats, not one quick dart: (1) a little copy of the product
  // pops up and out of the tile, like it's hopping free; (2) it hangs there
  // a breath; (3) it falls, accelerating under its own weight, into the
  // landing target and is absorbed — `onLand` fires right at that moment.
  function flyToProgress(sourceImg, landingEl, onLand) {
    if (!sourceImg || !landingEl) {
      if (onLand) onLand();
      return;
    }

    // Respect the OS-level reduced-motion setting (iOS, macOS, Windows and
    // Android all expose this) — skip straight to landing rather than
    // forcing motion on someone who has explicitly asked for less of it
    // system-wide.
    if (prefersReducedMotion) {
      if (onLand) onLand();
      return;
    }

    // Force layout to settle first (the sticky bar may have just been
    // revealed by the setQty call right before this) so both rects reflect
    // real, final positions rather than a mid-reflow snapshot — a common
    // source of a slightly-off landing spot on lower-powered/Android
    // devices where layout and paint can lag a frame behind script.
    void document.body.offsetHeight;

    var startRect = sourceImg.getBoundingClientRect();
    var endRect = landingEl.getBoundingClientRect();
    if (startRect.width === 0 || startRect.height === 0) return;
    // Last line of defense against a genuinely degenerate target rect (an
    // element that's technically in the DOM but not actually rendered
    // anywhere — display:none somewhere in its ancestor chain, or not yet
    // laid out) — getBoundingClientRect() returns all-zero for that case,
    // which previously sent the flying clone straight to the viewport's
    // literal top-left corner (0, 0) instead of skipping the animation
    // outright. Committing immediately, with no fly animation, reads far
    // better than a visibly wrong flight to a nonsense spot.
    if (endRect.top === 0 && endRect.left === 0 && endRect.width === 0 && endRect.height === 0) {
      if (onLand) onLand();
      return;
    }

    var clone = sourceImg.cloneNode(false);
    // Drop responsive-image attributes — with an explicit pixel width/
    // height already set below, srcset/sizes serve no purpose here and can
    // otherwise cause the browser to swap in a different source mid-flight
    // (a visible flicker), particularly on iOS Safari.
    clone.removeAttribute('srcset');
    clone.removeAttribute('sizes');
    clone.style.position = 'fixed';
    clone.style.left = startRect.left + 'px';
    clone.style.top = startRect.top + 'px';
    clone.style.width = startRect.width + 'px';
    clone.style.height = startRect.height + 'px';
    clone.style.margin = '0';
    clone.style.borderRadius = '50%';
    clone.style.objectFit = 'cover';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '1';
    // Promotes the clone to its own compositor layer up front, instead of
    // triggering that promotion mid-transition — the difference between a
    // smooth animation and a visible stutter on the first frame, especially
    // on lower-end Android hardware.
    clone.style.willChange = 'transform, opacity';
    clone.style.backfaceVisibility = 'hidden';
    // Phase 1: pop up and out of the tile — a small hop, not a shrink in
    // place. Ease-out so it feels like it springs up quickly then settles.
    clone.style.transition = 'transform 0.35s ease-out';
    document.body.appendChild(clone);

    var deltaX = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
    var deltaY = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);
    var popUpDistance = Math.max(30, startRect.height * 0.7);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        clone.style.transform = 'translateY(-' + popUpDistance + 'px) scale(0.55)';
      });
    });

    var popUpFallback = window.setTimeout(startFall, 450);

    clone.addEventListener(
      'transitionend',
      function onPopUp() {
        clone.removeEventListener('transitionend', onPopUp);
        window.clearTimeout(popUpFallback);
        // Falls immediately — no pause at the top of the hop. A held pause
        // here read as a stutter/stall rather than a smooth toss.
        startFall();
      },
      { once: true }
    );

    function startFall() {
      // Phase 3: falls, accelerating (ease-in), into the progress bar and
      // is absorbed. Slow and deliberate, not a quick dart. Opacity only
      // starts fading right at the very end of the fall, once it has
      // visually reached the bar — not partway through the drop, which
      // read as vanishing in mid-air before landing.
      clone.style.transition = 'transform 0.8s cubic-bezier(0.55, 0.06, 0.68, 0.19), opacity 0.12s ease 0.7s';
      clone.style.transform = 'translate(' + deltaX + 'px, ' + deltaY + 'px) scale(0.05)';
      clone.style.opacity = '0';
      settleOnce(clone, onLand, 900);
    }
  }

  function init(root) {
    // Multiple tiers (auto-upsell) when the page's box_tiers metafield is
    // set — e.g. "3 for ₹399" AND "6 for ₹699" for the same product pool.
    // Falls back to a single tier built from the legacy data-target/
    // data-discount-type/data-discount-value attributes when box_tiers
    // isn't set. Always sorted ascending by quantity.
    var tiers = [];
    try {
      tiers = JSON.parse(root.dataset.tiers || 'null') || [];
    } catch (e) {
      tiers = [];
    }
    tiers = tiers.filter(function (t) {
      return t && t.quantity > 0;
    });
    if (tiers.length === 0) {
      tiers = [
        {
          quantity: parseInt(root.dataset.target, 10) || 1,
          discount_type: root.dataset.discountType || 'fixed',
          discount_value: root.dataset.discountValue || 0
        }
      ];
    }
    tiers.sort(function (a, b) {
      return a.quantity - b.quantity;
    });
    var maxTier = tiers[tiers.length - 1];

    function tierLabel(tier) {
      return tier.discount_type === 'percentage'
        ? 'Buy ' + tier.quantity + ' at ' + tier.discount_value + '% off'
        : 'Buy ' + tier.quantity + ' at ' + formatMoney((parseFloat(tier.discount_value) || 0) * 100);
    }

    // The highest tier whose quantity has been reached (or exceeded), or
    // null if the box hasn't reached even the first tier yet.
    function currentTier(qty) {
      var current = null;
      tiers.forEach(function (t) {
        if (qty >= t.quantity) current = t;
      });
      return current;
    }

    // The next tier still ahead of the current quantity — what the upsell
    // copy points toward — or null once the highest tier is reached.
    function nextTier(qty) {
      for (var i = 0; i < tiers.length; i++) {
        if (tiers[i].quantity > qty) return tiers[i];
      }
      return null;
    }

    // variantId -> quantity. Purely a local, page-only staging area — see
    // the comment above submitBoxToCart() further down.
    //
    // Declared this early because updateMobileStickyVisibility (below)
    // reads totalQty() before this point in the function otherwise runs —
    // a bare `var quantities` further down is hoisted but still undefined
    // until its assignment line executes, so calling totalQty() first threw
    // and aborted the rest of init() (tile +/- wiring, renderAll(), all of
    // it).
    var quantities = {};

    // FLAT, per-UNIT add order — one entry per unit currently in the box,
    // in the exact order each individual unit was tapped in. This (not a
    // per-PRODUCT order with quantities grouped under it) is what makes the
    // thumbnail strip a true "next empty slot" timeline: picking A, then B,
    // then A again lays out A, B, A — the second A does NOT jump back next
    // to the first A. A per-product order used to do exactly that (every
    // unit of a product rendered consecutively wherever that product FIRST
    // appeared in the order), which broke the moment a product already in
    // the box was added to again after something else. Each `setQty` call
    // pushes/removes exactly as many entries as the quantity actually
    // changed by (see setQty below); which specific occurrence gets removed
    // doesn't matter since same-id units are interchangeable.
    var unitOrder = [];

    var tiles = root.querySelectorAll('[data-idz-box-tile]');
    // id -> tile lookup, since the thumbnail strip iterates unitOrder (flat
    // per-unit add order), not `tiles` (grid order) directly.
    var tileById = {};
    tiles.forEach(function (t) {
      tileById[t.dataset.variantId] = t;
    });
    var progressEl = root.querySelector('[data-idz-box-progress]');
    var offerEl = root.querySelector('[data-idz-box-offer]');
    var offerDefaultText = offerEl ? offerEl.textContent.trim() : '';

    // The sticky bar lives as a sibling right after `root` (see
    // idz-build-a-box.liquid) rather than inside it, so it stays fixed to
    // the viewport rather than scrolling away with a tall product grid.
    var stickyBar = root.nextElementSibling;
    if (!stickyBar || !stickyBar.hasAttribute('data-idz-box-sticky')) stickyBar = null;
    // Lives in the sticky bar, not the page body, for the same reason —
    // guaranteed to still be on screen wherever the tap that triggered an
    // error happened, on a product grid that can run far taller than a
    // screen.
    var errorEl = stickyBar ? stickyBar.querySelector('[data-idz-box-error]') : null;
    var stickyRibbonEl = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-ribbon]') : null;
    var stickyProgressEl = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-progress]') : null;
    var stickyRailEl = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-rail]') : null;
    var stickySubtotalEl = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-subtotal]') : null;
    var stickyCheckout = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-checkout]') : null;
    var stickyCheckoutHint = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-checkout-hint]') : null;
    var stickyThumbs = stickyBar ? stickyBar.querySelector('[data-idz-box-sticky-thumbs]') : null;

    /* ----------------------------------------------------------------------
     * Real-cart plumbing — same contract idz-cart.js uses site-wide
     * (cart-drawer's own getSectionsToRender()/renderContents pattern), a
     * small local copy rather than a shared export since idz-cart.js keeps
     * everything inside its own closure. Kept deliberately silent (updates
     * the drawer's own markup in the background, never calls cart.open())
     * so rapid taps across several tiles don't pop the drawer open on every
     * single one — "View cart" (below) is the one deliberate way to open it.
     * -------------------------------------------------------------------- */

    function showBoxError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearBoxError() {
      if (errorEl) errorEl.hidden = true;
    }

    // Nothing here touches the real cart on a tap any more — `quantities`
    // is a purely local, page-only staging area. The ONE place this page
    // ever calls /cart/add.js is submitBoxToCart() (further down), fired
    // by the "Checkout" button: every picked item is added in a single
    // bulk request at that point, then the customer is sent straight to
    // checkout. Until that tap, nothing has been added to the cart yet.
    var boxSubmitting = false;

    function submitBoxToCart() {
      if (boxSubmitting) return;
      var qty = totalQty();
      var reached = currentTier(qty);
      // A unique id per checkout (not per product) — this is what
      // idz-box-cart-groups.js groups the resulting cart lines by by, and
      // what a later, separate box purchase must NOT collide with, so two
      // boxes bought in different visits/sessions stay distinct groups.
      // Deterministic, not random: derived from what is IN the box (each
      // variant with its quantity) plus the box label. Buying the exact same
      // box a second time then produces lines with identical properties, which
      // Shopify merges into the existing lines (quantity goes up) instead of
      // adding a separate second box; the cart groups that as "2 of this box".
      // A box with different contents gets a different id, so it stays its own
      // line. (Older boxes added before this change keep their random ids and
      // won't merge with new ones.)
      var boxSignature =
        (root.dataset.boxLabel || 'Box') +
        '|' +
        Object.keys(quantities)
          .filter(function (id) {
            return quantities[id] > 0;
          })
          .sort()
          .map(function (id) {
            return id + ':' + quantities[id];
          })
          .join(',');
      var boxHash = 5381;
      for (var hi = 0; hi < boxSignature.length; hi++) {
        boxHash = ((boxHash * 33) ^ boxSignature.charCodeAt(hi)) >>> 0;
      }
      var boxId = 'box-' + boxHash.toString(36);
      var boxLabel = root.dataset.boxLabel || 'Box';
      // The preview price at the tier actually reached when "Checkout" was
      // tapped — same discountedTotal() used for the on-page preview, just
      // captured once here so idz-box-cart-groups.js can show the SAME
      // number in the cart rather than recomputing it (the underlying real
      // lines keep their own real prices; this is a display-only figure).
      var boxPrice = reached ? discountedTotal(baseTotal(), reached.discount_type, reached.discount_value) : baseTotal();

      var items = Object.keys(quantities)
        .filter(function (id) {
          return quantities[id] > 0;
        })
        .map(function (id) {
          return {
            id: id,
            quantity: quantities[id],
            properties: {
              // "_" prefix keeps Dawn's own cart templates from ever
              // printing these as visible line-item options.
              '_Box Id': boxId,
              '_Box Label': boxLabel,
              '_Box Price': String(boxPrice),
              // This product's own quantity for ONE box — what
              // idz-box-cart-groups.js's bundle quantity stepper scales up/
              // down together across every line in the group when the
              // customer asks for 2+ of this same box.
              '_Box Unit Qty': String(quantities[id])
            }
          };
        });
      if (items.length === 0) return;

      function setCheckoutLoading(isLoading) {
        if (!stickyCheckout) return;
        stickyCheckout.disabled = isLoading;
        stickyCheckout.classList.toggle('loading', isLoading);
        var spinner = stickyCheckout.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      boxSubmitting = true;
      setCheckoutLoading(true);
      clearBoxError();

      var cart = document.querySelector('cart-drawer');
      var body = { items: items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart
          .getSectionsToRender()
          .map(function (section) {
            return section.id;
          })
          .join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (data.status) {
            showBoxError(data.description || data.message || 'Could not add your box to the cart.');
            boxSubmitting = false;
            setCheckoutLoading(false);
            return;
          }
          // Success — refresh the drawer's own markup with the sections
          // just returned (so it already shows the collapsed box row, no
          // second round-trip) and open it, instead of jumping straight to
          // Shopify checkout. The customer proceeds from the drawer's own
          // checkout button when they're ready.
          if (cart && data.sections) {
            cart.getSectionsToRender().forEach(function (section) {
              var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
              if (!el || !data.sections[section.id]) return;
              var parsed = new DOMParser()
                .parseFromString(data.sections[section.id], 'text/html')
                .querySelector(section.selector || '.shopify-section');
              if (parsed) el.innerHTML = parsed.innerHTML;
            });
            // Replacing #CartDrawer's innerHTML above destroys and
            // recreates #CartDrawer-Overlay — along with the click-to-close
            // listener Dawn's cart-drawer.js bound to the OLD element in
            // its constructor. Rebind it, or clicking outside the drawer
            // silently stops closing it after this point.
            if (typeof cart.close === 'function') {
              var overlay = cart.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
            }
            // We just successfully added items, so the cart can never be
            // empty at this point — but nothing above clears the "empty
            // cart" classes idz-cart.js otherwise manages, so a drawer
            // that was empty before this tap would still show its empty
            // state for a moment after re-opening. /cart/add.js's own
            // response (unlike /cart.js or /cart/change.js) doesn't carry
            // a cart-level item_count to check, so just clear them
            // unconditionally here.
            cart.classList.remove('is-empty');
            var innerEl = cart.querySelector('.drawer__inner');
            if (innerEl) innerEl.classList.remove('is-empty');
            var itemsEl = cart.querySelector('cart-drawer-items');
            if (itemsEl) itemsEl.classList.remove('is-empty');
          }
          // Collapse the box's own lines into one row BEFORE the drawer
          // opens — idz-box-cart-groups.js otherwise only re-runs on its
          // own next-frame schedule (via a MutationObserver), which left a
          // brief visible flash of the real, un-grouped product lines the
          // instant the drawer appeared.
          if (window.IdzBoxCartGroups && typeof window.IdzBoxCartGroups.run === 'function') {
            window.IdzBoxCartGroups.run();
          }
          if (cart && typeof cart.open === 'function') cart.open();
          // idz-cart.js's own listeners don't see this request (it's ours,
          // not theirs), so tell the rest of the page the cart changed —
          // idz-box-cart-groups.js listens for this to re-run its grouping
          // against the freshly-opened drawer. Real variantId -> quantity
          // map, same shape idz-cart.js itself broadcasts, not just an
          // empty stand-in (other listeners, like the sticky add-to-cart
          // fallback, depend on this being accurate).
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .then(function (fullCart) {
              var realQuantities = {};
              fullCart.items.forEach(function (item) {
                realQuantities[item.variant_id] = (realQuantities[item.variant_id] || 0) + item.quantity;
              });
              document.dispatchEvent(new CustomEvent('idz:cart-quantities', { detail: realQuantities }));
            })
            .catch(function () {});
          boxSubmitting = false;
          setCheckoutLoading(false);
        })
        .catch(function () {
          showBoxError('Could not add your box to the cart. Please try again.');
          boxSubmitting = false;
          setCheckoutLoading(false);
        });
    }

    // Mobile only: the sticky bar starts hidden (via the
    // `idz-box-sticky--pending` class already in the rendered markup, so
    // there's no flash-then-hide while this script loads). Kept simple —
    // just two rules: show it once the page is scrolled past 7% of its
    // height, or show it right away regardless of scroll position if the
    // box already has anything in it. Scrolling back above 7% (with an
    // empty box) hides it again. Desktop never gets the class in the first
    // place, having no effect there (see the `@media (max-width: 749px)`
    // guard in the CSS).
    var mobileMq = window.matchMedia('(max-width: 749px)');
    function updateMobileStickyVisibility() {
      if (!stickyBar) return;
      if (!mobileMq.matches) {
        stickyBar.classList.remove('idz-box-sticky--pending');
        return;
      }
      var scrollable = document.documentElement.scrollHeight - window.innerHeight;
      var scrolledPct = scrollable > 0 ? window.scrollY / scrollable : 0;
      var shouldShow = scrolledPct > 0.07 || totalQty() > 0;
      stickyBar.classList.toggle('idz-box-sticky--pending', !shouldShow);
    }
    window.addEventListener('scroll', updateMobileStickyVisibility, { passive: true });
    window.addEventListener('resize', updateMobileStickyVisibility);
    if (typeof mobileMq.addEventListener === 'function') {
      mobileMq.addEventListener('change', updateMobileStickyVisibility);
    }
    updateMobileStickyVisibility();

    // Generic "floats above, fades itself out" popup — was originally
    // shared between the old separate "View box" toggle and this button;
    // "View box" has since been removed entirely and all of its behavior
    // (expand/collapse the summary, this hint popup, the label swap) now
    // lives only on "View cart" below.
    function showHint(hintEl, timerRef) {
      if (!hintEl) return timerRef;
      window.clearTimeout(timerRef);
      hintEl.hidden = false;
      hintEl.classList.remove('is-visible');
      void hintEl.offsetWidth; // restart the fade if clicked again mid-fade
      hintEl.classList.add('is-visible');
      return window.setTimeout(function () {
        hintEl.classList.remove('is-visible');
        window.setTimeout(function () {
          hintEl.hidden = true;
        }, 200);
      }, 2000);
    }

    // Tracks the highest tier index reached so far, so confetti only fires
    // the moment a NEW tier is crossed — not on every render while sitting
    // at a tier already reached.
    var wasTierIndex = -1;

    function totalQty() {
      return Object.keys(quantities).reduce(function (sum, id) {
        return sum + quantities[id];
      }, 0);
    }

    function baseTotal() {
      var sum = 0;
      tiles.forEach(function (tile) {
        var id = tile.dataset.variantId;
        var qty = quantities[id] || 0;
        if (qty > 0) sum += qty * (parseFloat(tile.dataset.price) || 0);
      });
      return sum;
    }

    function renderTiles() {
      var atMax = totalQty() >= maxTier.quantity;
      tiles.forEach(function (tile) {
        var id = tile.dataset.variantId;
        var qty = quantities[id] || 0;
        var qtyEl = tile.querySelector('[data-idz-box-qty]');
        var addBtn = tile.querySelector('[data-idz-box-add]');
        var plusBtn = tile.querySelector('[data-idz-box-plus]');
        var minusBtn = tile.querySelector('[data-idz-box-minus]');
        var busy = tile.dataset.busy === 'true';
        var available = tile.dataset.available === 'true';
        if (qtyEl) qtyEl.textContent = qty;
        if (minusBtn) minusBtn.disabled = qty <= 0 || busy;
        // Capped at the HIGHEST tier's quantity — below that, adding more
        // of anything is always allowed (no cap at a lower tier, since the
        // whole point is upselling toward the max), but once the top tier
        // is reached there's nothing higher to upsell toward, so adding
        // stops.
        if (plusBtn) plusBtn.disabled = !available || busy || atMax;
        if (addBtn) addBtn.disabled = !available || busy || atMax;
        // .idz-box__tile--selected (qty > 0) is what actually swaps the
        // "Add to box" button for the stepper — see idz-theme-v2.css.
        tile.classList.toggle('idz-box__tile--selected', qty > 0);
      });
    }

    // The in-page line above the product grid (not the sticky bar's own
    // ribbon/progress — see renderSticky() for that). Simple "N to choose"
    // style, always pointed at the next tier ahead (or the top tier once
    // there's nothing left to upsell toward).
    function renderProgress() {
      if (!progressEl) return;
      var qty = totalQty();
      var upNext = nextTier(qty);
      if (upNext) {
        var remaining = upNext.quantity - qty;
        progressEl.textContent =
          'Add ' + remaining + ' more item' + (remaining === 1 ? '' : 's') + ' to ' + tierLabel(upNext);
      } else {
        progressEl.textContent = "You've unlocked our best offer!";
      }
    }

    // The offer line reads as a soft, inviting "pick any N for ₹X" — the
    // way you'd describe a deal out loud — rather than a stark "Box price:
    // ₹899" label-and-number pair. Once a tier is reached it shifts to
    // confirming the actual total the customer is about to pay at that
    // tier's price.
    function renderTotal() {
      if (!offerEl) return;
      var qty = totalQty();
      var reached = currentTier(qty);
      if (reached) {
        var discounted = discountedTotal(baseTotal(), reached.discount_type, reached.discount_value);
        offerEl.textContent = 'Your box: ' + formatMoney(discounted);
        return;
      }
      offerEl.textContent = offerDefaultText;
    }

    // Thumbnail strip — one small square per UNIT picked (qty 3 of the
    // same product shows as three separate thumbnails, not one thumbnail
    // with a "3x" badge — easier to see and remove exactly what's in the
    // box at a glance), in the FLAT order each individual UNIT was added
    // (unitOrder — not grouped by product, and not `tiles`' fixed grid
    // order), followed by exactly ONE empty "+" placeholder square for the
    // next slot still open (not the whole remaining ceiling at once — see
    // the comment further down). Hovering (desktop) or tapping (touch) a
    // filled thumb shows a small tooltip with the product's image and name
    // — this is now the only place to check exactly what's in the box,
    // since the old expandable summary list/"View cart" button has been
    // removed.
    function renderThumbs() {
      if (!stickyThumbs) return;

      // Nothing picked yet — the whole strip (real thumbs AND the empty
      // placeholder ceiling) has no reason to exist until there's a first
      // item to actually anchor it to. Hidden outright rather than left
      // showing an empty row of "+" squares.
      stickyThumbs.hidden = totalQty() === 0;
      if (stickyThumbs.hidden) {
        stickyThumbs.innerHTML = '';
        return;
      }

      stickyThumbs.innerHTML = '';

      unitOrder.forEach(function (id) {
        var tile = tileById[id];
        if (!tile) return;

        var thumb = document.createElement('div');
        thumb.className = 'idz-box-sticky__thumb';
        thumb.dataset.thumbId = id;
        thumb.tabIndex = 0;

        if (tile.dataset.image) {
          var img = document.createElement('img');
          img.src = tile.dataset.image;
          img.alt = tile.dataset.title || '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        }

        var tooltip = document.createElement('div');
        tooltip.className = 'idz-box-sticky__thumb-tooltip';
        if (tile.dataset.image) {
          var tooltipImg = document.createElement('img');
          tooltipImg.src = tile.dataset.image;
          tooltipImg.alt = '';
          tooltipImg.loading = 'lazy';
          tooltip.appendChild(tooltipImg);
        }
        var tooltipText = document.createElement('span');
        tooltipText.textContent = tile.dataset.title || '';
        tooltip.appendChild(tooltipText);
        thumb.appendChild(tooltip);

        // Tap/click toggles the tooltip open (for touch, which has no
        // hover) — CSS handles the hover case on its own. Only one open
        // at a time; tapping elsewhere on the page closes it.
        thumb.addEventListener('click', function (event) {
          event.stopPropagation();
          var wasOpen = thumb.classList.contains('is-open');
          stickyThumbs.querySelectorAll('.idz-box-sticky__thumb.is-open').forEach(function (open) {
            open.classList.remove('is-open');
          });
          if (!wasOpen) thumb.classList.add('is-open');
        });

        // Close/remove icon — removes exactly ONE unit of this product
        // (this one thumbnail), not the whole line — matching "each unit
        // is its own thumbnail" instead of removing every unit at once.
        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'idz-box-sticky__thumb-remove';
        removeBtn.setAttribute('aria-label', 'Remove one ' + (tile.dataset.title || 'item') + ' from box');
        removeBtn.textContent = '−';
        removeBtn.addEventListener('click', function (event) {
          event.stopPropagation();
          setQty(id, (quantities[id] || 0) - 1);
        });
        thumb.appendChild(removeBtn);

        stickyThumbs.appendChild(thumb);
      });

      // Only the very NEXT empty slot shows, one at a time — not the whole
      // remaining ceiling up to the highest tier at once (that used to
      // dump e.g. 5 "+" placeholders on screen after the very first tap).
      // Each add reveals exactly one more "+" square, same as each add
      // fills exactly one more thumb — the placeholder line grows in step
      // with the filled line instead of being front-loaded.
      if (totalQty() < maxTier.quantity) {
        var placeholder = document.createElement('span');
        placeholder.className = 'idz-box-sticky__thumb idz-box-sticky__thumb--empty';
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.textContent = '+';
        stickyThumbs.appendChild(placeholder);
      }
    }

    // Animates the sticky bar's ink rail smoothly between percentages
    // instead of snapping straight to the new value — same rAF-driven
    // gradient-recompute technique idz-cart-progress.js's animateSlide
    // uses for the side cart's own reward bar, kept as its own small copy
    // here rather than shared (this file has no build step to import
    // from, and the theme has no bundler). `stickyRailLastPercent` (module-
    // level, below) remembers where the last animation actually left off,
    // so a tap that lands mid-animation restarts smoothly from wherever
    // the rail visually is, not from the last fully-settled value.
    var stickyRailLastPercent = null;

    function stickyRailGradient(percent, ink, surface) {
      return (
        'linear-gradient(to right, ' +
        ink +
        ' 0%, ' +
        ink +
        ' ' +
        percent +
        '%, ' +
        surface +
        ' ' +
        percent +
        '%, ' +
        surface +
        ' 100%)'
      );
    }

    function animateStickyRail(targetPercent, atMax, ink, surface) {
      if (!stickyRailEl) return;

      // Once every tier is met, the rail goes hard solid ink, full stop —
      // no gradient/animation left to run, same as idz-cart-progress.css's
      // own `[data-idz-cr-all-achieved]` rule for the side cart's bar.
      if (atMax) {
        stickyRailEl.style.background = ink;
        stickyRailLastPercent = 100;
        return;
      }

      var start = stickyRailLastPercent === null ? targetPercent : stickyRailLastPercent;
      stickyRailLastPercent = targetPercent;

      if (start === targetPercent) {
        stickyRailEl.style.background = stickyRailGradient(targetPercent, ink, surface);
        return;
      }

      var duration = 500;
      var startTime = null;

      function frame(now) {
        if (startTime === null) startTime = now;
        var t = Math.min((now - startTime) / duration, 1);
        // easeOutCubic — starts fast, settles gently into the new value,
        // same feel as idz-cart-progress.js's own ease().
        var eased = 1 - Math.pow(1 - t, 3);
        var value = start + (targetPercent - start) * eased;
        stickyRailEl.style.background = stickyRailGradient(value, ink, surface);
        if (t < 1) {
          window.requestAnimationFrame(frame);
        } else {
          // Land exactly on the real target — floating-point easing can
          // leave this a hair short of it otherwise.
          stickyRailEl.style.background = stickyRailGradient(targetPercent, ink, surface);
        }
      }
      window.requestAnimationFrame(frame);
    }

    function renderSticky() {
      if (!stickyBar) return;
      var qty = totalQty();
      var reached = currentTier(qty);
      var upNext = nextTier(qty);

      renderThumbs();

      // "Checkout" appears as soon as the FIRST tier is reached (not only
      // the top one) — every tier is a valid, checkout-able box; the
      // upsell just keeps going for whichever tiers are still ahead.
      stickyBar.classList.toggle('idz-box-sticky--ready', !!reached);

      if (stickyRibbonEl) {
        if (reached) {
          stickyRibbonEl.textContent = tierLabel(reached) + ' applied';
          stickyRibbonEl.hidden = false;
        } else {
          stickyRibbonEl.hidden = true;
        }
      }
      if (stickyProgressEl) {
        // Once every tier is reached there's nothing left to upsell toward
        // — the line disappears entirely instead of showing a "you got the
        // best offer" message, per the requested wording. The ribbon (just
        // above) keeps showing which tier is applied on its own.
        if (upNext) {
          var remaining = upNext.quantity - qty;
          // "More" only makes sense once something's actually in the bag
          // (qty > 0) — at qty 0 it read as if items were already picked
          // ("Add 2 More Item(s)" with an empty box), so the very first
          // message just says "Add 2 Item(s)" instead.
          var verb = qty > 0 ? 'Add ' + remaining + ' More Item(s)' : 'Add ' + remaining + ' Item(s)';
          stickyProgressEl.textContent = verb + ' to ' + tierLabel(upNext);
          stickyProgressEl.hidden = false;
        } else {
          stickyProgressEl.textContent = '';
          stickyProgressEl.hidden = true;
        }
      }
      if (stickyRailEl) {
        // Rides toward whichever milestone is next (or the top tier once
        // there's nothing left ahead), same as the ribbon/progress line.
        // Painted as a hard-edged linear-gradient directly on the rail
        // itself (same technique as the cart drawer's own progress bar,
        // idz-cart-progress.js's railGradient) rather than a child
        // element's width — a gradient is 100% of its own box by
        // construction, so there's no parent/child width relationship
        // that could silently fail to resolve.
        var fillTarget = upNext ? upNext.quantity : maxTier.quantity;
        var atMax = qty >= maxTier.quantity;
        var percent = Math.min(100, Math.round((qty / fillTarget) * 100));
        // Reads the ink colour straight off the same CSS custom property
        // the rail's own inline `border-color` (see idz-build-a-box.liquid)
        // is set from, rather than round-tripping through
        // getComputedStyle(...).borderColor — that round trip depends on
        // the browser having already resolved the inline style's
        // `var(--idz-brand-ink, ...)` into a real computed colour by the
        // moment this runs, and on `border-color` even being a color the
        // browser is willing to report back accurately for an element with
        // a 0.15rem border (some engines can round-trip this fine, some
        // don't) — either gap silently left `ink` as an empty string,
        // painting a transparent-on-transparent gradient that was there,
        // just invisible. Reading the custom property itself the same way
        // `surface` already does is direct and doesn't depend on any of
        // that.
        // .trim() matters here specifically — getPropertyValue commonly
        // returns a value with a leading space (the property is authored
        // as `--idz-brand-ink: {{ settings.idz_ink }};`, space after the
        // colon included verbatim), and while a stray leading space alone
        // usually still parses fine inside a CSS value, it's one less
        // variable if this rail is ever debugged again — and a genuinely
        // EMPTY string (the property missing/not yet resolved) still
        // falls through to the hard-coded fallback either way.
        var ink = (getComputedStyle(document.documentElement).getPropertyValue('--idz-brand-ink') || '').trim() || '#241b4e';
        var surface = (getComputedStyle(document.documentElement).getPropertyValue('--idz-brand-surface') || '').trim() || '#ffffff';
        animateStickyRail(atMax ? 100 : percent, atMax, ink, surface);
      }
      if (stickySubtotalEl) {
        var subtotal = reached
          ? discountedTotal(baseTotal(), reached.discount_type, reached.discount_value)
          : baseTotal();
        stickySubtotalEl.textContent = formatMoney(subtotal);
      }
      // "Checkout" — the one button that actually adds anything to the
      // real cart (see submitBoxToCart()). Stays visually present but
      // toned down and non-functional while the box is empty, and wears
      // the same gentle "tap me" glow the old "View box" button used to
      // until pressed once this visit.
      if (stickyCheckout) {
        stickyCheckout.classList.toggle('idz-box-sticky__submit--empty', qty === 0);
        stickyCheckout.classList.toggle(
          'idz-box-sticky__submit--pulse',
          qty > 0 && !stickyCheckout.classList.contains('idz-box-sticky__submit--seen')
        );
      }

      // Fires again for each NEW tier reached (not just the first), so
      // climbing from "3 for ₹399" to "6 for ₹699" gets its own moment —
      // not just once ever, and not on every render while sitting still at
      // a tier already reached.
      var reachedIndex = reached ? tiers.indexOf(reached) : -1;
      if (reachedIndex > wasTierIndex) confetti();
      wasTierIndex = reachedIndex;
    }

    function renderAll() {
      renderTiles();
      renderProgress();
      renderTotal();
      renderSticky();
    }

    function setQty(id, qty) {
      qty = Math.max(0, qty);
      var previous = quantities[id] || 0;
      if (qty === 0) {
        delete quantities[id];
      } else {
        quantities[id] = qty;
      }

      // Keep unitOrder's length/contents in sync with the actual quantity
      // change. Growing pushes new entries onto the END (so a new unit
      // always appends after whatever's already in the box, regardless of
      // whether this product has other units earlier in the strip).
      // Shrinking removes that many entries for this id — which specific
      // occurrence doesn't matter since same-id units are interchangeable,
      // so the last one is simplest.
      if (qty > previous) {
        for (var addN = 0; addN < qty - previous; addN++) unitOrder.push(id);
      } else if (qty < previous) {
        for (var removeN = 0; removeN < previous - qty; removeN++) {
          var lastIndex = unitOrder.lastIndexOf(id);
          if (lastIndex !== -1) unitOrder.splice(lastIndex, 1);
        }
      }

      updateMobileStickyVisibility();
      renderAll();
    }

    // Shared by the "Add to box" button and the tile's own + stepper button
    // — both add exactly one unit the same way (purely local — see the
    // comment above submitBoxToCart()), they just show up in different
    // states (see .idz-box__tile--selected in idz-theme-v2.css).
    // Where a NEW unit will actually land in the thumbnail strip — since
    // unitOrder is a flat per-unit timeline and every new unit is always
    // APPENDED to the end of it (never inserted among earlier units, same
    // product or not), its landing index is simply the current total
    // quantity, i.e. right before the first empty placeholder.
    function computeLandingElement() {
      if (!stickyThumbs) return null;
      // The very first unit added has nothing to land ON yet — the strip
      // is still `hidden` with no children at all (renderThumbs only
      // reveals it once totalQty() > 0, which hasn't happened yet at the
      // point this runs, BEFORE the deferred setQty() that will actually
      // add this unit). The strip's own container element, even freshly
      // un-hidden, can still measure as a zero-size point (an empty flex/
      // grid item with no content and no explicit size has nothing to
      // give it real width/height) — which sent the flying clone toward
      // literally the viewport's (0, 0) corner exactly when that
      // container's rect came back all-zero. The progress rail right
      // above it (data-idz-box-sticky-rail) is a much safer target for
      // this one case: it always has a real, explicit height/border and
      // is never hidden, so it's guaranteed to have a real, non-zero rect
      // to land on — landing there instead of on the (possibly
      // zero-sized) empty strip still puts the clone right next to where
      // its thumbnail is about to appear.
      if (stickyThumbs.hidden || !stickyThumbs.children.length) {
        return stickyRailEl || stickyThumbs;
      }
      var index = totalQty();
      var children = stickyThumbs.children;
      return children[index] || children[children.length - 1] || null;
    }

    // Polls the sticky bar's own on-screen position across animation
    // frames and waits until it stops changing before calling `cb` — used
    // right below, after clearing `idz-box-sticky--pending`, since that
    // class removal starts a 0.25s CSS transition (transform/opacity, see
    // the CSS) rather than repositioning the bar instantly. Measuring
    // landingEl's rect synchronously, in the same tick the class was
    // removed, caught the bar still at its OLD (off-screen, translateY
    // 100%) position — the transition hadn't had a chance to actually
    // move anything yet — which is what sent the flying clone toward the
    // wrong corner of the screen on the very first tap. Same
    // wait-until-stable technique idz-cart-progress.js's
    // whenPanelSettled() uses for the identical class of problem on the
    // side cart's own milestone star. Costs at most 2 frames (~33ms,
    // imperceptible) when the bar was already visible and not moving,
    // which is the common case for every tap after the first.
    function whenStickyBarSettled(cb) {
      if (!stickyBar) {
        cb();
        return;
      }
      var lastTop = null;
      var stableFrames = 0;
      var frames = 0;
      var maxFrames = 24; // ~0.4s safety cap at 60fps

      function step() {
        frames++;
        var rect = stickyBar.getBoundingClientRect();
        if (lastTop !== null && Math.abs(rect.top - lastTop) < 0.5) {
          stableFrames++;
        } else {
          stableFrames = 0;
        }
        lastTop = rect.top;

        if (stableFrames >= 2 || frames >= maxFrames) {
          cb();
          return;
        }
        window.requestAnimationFrame(step);
      }
      window.requestAnimationFrame(step);
    }

    function addOne(tile, id) {
      var img = tile.querySelector('img');
      var previous = quantities[id] || 0;

      // Confetti is a separate, immediate celebration — fires the instant
      // this tap crosses into a new tier, not delayed until the fly-to-
      // thumbnail animation below actually lands (~1.15s later). Updates
      // wasTierIndex right away too, so renderSticky's own crossing check
      // (inside commit(), once the deferred setQty finally runs) sees no
      // NEW crossing left to re-fire for the same tap.
      var newTotal = totalQty() + 1;
      var currentReached = currentTier(totalQty());
      var newReached = currentTier(newTotal);
      var currentIndex = currentReached ? tiers.indexOf(currentReached) : -1;
      var newIndex = newReached ? tiers.indexOf(newReached) : -1;
      if (newIndex > currentIndex && newIndex > wasTierIndex) {
        confetti();
        wasTierIndex = newIndex;
      }

      // On mobile, the WHOLE sticky bar can still be sitting in its
      // "pending" state at this exact moment — `idz-box-sticky--pending`
      // (see the CSS) transforms it translateY(100%) + opacity: 0, i.e.
      // genuinely off-screen below the viewport, not just invisible in
      // place. updateMobileStickyVisibility() is what normally clears that
      // class, but it keys off totalQty() > 0 — which, for THIS very tap,
      // is still 0 (setQty hasn't run yet; it's deferred until the fly
      // animation lands, in commit() further down) — so calling it here
      // wouldn't actually clear the class yet either. Since this tap IS
      // about to make totalQty() > 0 regardless, the class is cleared
      // directly here instead of going through that stale check. Without
      // this, the very first tap on mobile (before any scroll) measured
      // every element inside the bar — including the thumbnail strip
      // below — at its translated-away, off-screen position, which is
      // what actually sent the flying clone to the wrong corner of the
      // screen instead of near the sticky bar.
      if (stickyBar) stickyBar.classList.remove('idz-box-sticky--pending');

      // The very first unit added has nowhere real to fly toward yet — the
      // thumbnail strip is still `hidden` (renderThumbs only reveals it
      // once something's actually in the box), so its bounding rect would
      // measure as all-zero and the flying clone would head for the
      // viewport's literal top-left corner instead of anywhere near the
      // sticky bar. Revealing it here, synchronously, BEFORE landingEl is
      // computed below, gives it real on-screen position for
      // getBoundingClientRect() to measure — it's still visually empty
      // for this one frame (no thumb children yet, that's fine), just no
      // longer `display: none`.
      if (stickyThumbs && stickyThumbs.hidden) stickyThumbs.hidden = false;

      function commit() {
        setQty(id, previous + 1);
        // The slot flyToProgress flew toward no longer exists once setQty
        // re-renders — find the fresh, now-real thumbnail for this product
        // and pulse THAT instead, so the landing bounce lands on something
        // actually on screen. Several may now match this id (each unit is
        // its own thumbnail) — the LAST one is always this newest unit,
        // since renderThumbs appends a tile's units consecutively.
        var freshThumbs = stickyThumbs ? stickyThumbs.querySelectorAll('[data-thumb-id="' + id + '"]') : [];
        var freshThumb = freshThumbs[freshThumbs.length - 1];
        if (freshThumb) pulseLanding(freshThumb);
      }

      // Waits for the sticky bar's own slide-in (see whenStickyBarSettled
      // above) before measuring anything — only then is landingEl's rect
      // (and everything flyToProgress computes from it) guaranteed to
      // reflect where the bar/thumbnail strip has actually settled.
      whenStickyBarSettled(function () {
        // Grabbed BEFORE anything changes — a real, currently-live element
        // in the DOM at exactly the spot the new thumbnail will occupy, so
        // the clone has somewhere accurate to fly toward and visually fall
        // into. setQty (which is what actually turns that spot into a real
        // filled thumbnail) is deferred until the animation lands (see
        // commit() above, passed as flyToProgress's onLand), instead of
        // firing immediately — that used to make the real thumbnail pop in
        // a full second before the flying copy arrived on top of it.
        var landingEl =
          computeLandingElement(id) || (stickyBar && !stickyBar.hidden && stickyCheckout) || document.querySelector('#cart-icon-bubble');

        if (img && landingEl) {
          flyToProgress(img, landingEl, commit);
        } else {
          commit();
        }
      });
    }

    tiles.forEach(function (tile) {
      var id = tile.dataset.variantId;
      var addBtn = tile.querySelector('[data-idz-box-add]');
      var plusBtn = tile.querySelector('[data-idz-box-plus]');
      var minusBtn = tile.querySelector('[data-idz-box-minus]');
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          addOne(tile, id);
        });
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          addOne(tile, id);
        });
      }
      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          var previous = quantities[id] || 0;
          if (previous <= 0) return;
          setQty(id, previous - 1);
        });
      }
    });

    // Closes any tapped-open thumbnail tooltip when the customer taps
    // anywhere else on the page (thumb clicks stop propagation above, so
    // this only ever fires for clicks outside the thumbnail strip).
    document.addEventListener('click', function () {
      if (!stickyThumbs) return;
      stickyThumbs.querySelectorAll('.idz-box-sticky__thumb.is-open').forEach(function (open) {
        open.classList.remove('is-open');
      });
    });

    var stickyCheckoutHintTimer = null;
    if (stickyCheckout) {
      stickyCheckout.addEventListener('click', function () {
        if (totalQty() === 0) {
          stickyCheckoutHintTimer = showHint(stickyCheckoutHint, stickyCheckoutHintTimer);
          return;
        }
        stickyCheckout.classList.add('idz-box-sticky__submit--seen');
        submitBoxToCart();
      });
    }

    renderAll();
  }

  function start() {
    document.querySelectorAll('[data-idz-box]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
